/**
 * Services Query Hooks — cached-instant rendering for ServicesScreen.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface ServicesListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  strategic_goal?: number;
  organizational_unit?: number;
  search?: string;
  year?: number;
}

interface ServicesListResponse {
  results: any[];
  count: number;
}

export function useServicesQuery(params?: ServicesListParams) {
  return useQuery({
    queryKey: queryKeys.services.list(params),
    queryFn: async (): Promise<ServicesListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.strategic_goal) queryParams.strategic_goal = params.strategic_goal;
      if (params?.organizational_unit)
        queryParams.organizational_unit = params.organizational_unit;
      if (params?.search) queryParams.search = params.search;
      if (params?.year) queryParams.year = params.year;

      return await servicesApi.list(queryParams);
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useServiceDetailQuery(serviceId: number) {
  return useQuery({
    queryKey: queryKeys.services.detail(serviceId),
    queryFn: () => servicesApi.get(serviceId),
    enabled: !!serviceId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteServiceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: number) => servicesApi.delete(serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
    },
  });
}

export default useServicesQuery;
